import React from "react";
import { Building2, Stethoscope } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// EL BANNER DE ALCANCE — UNA sola implementación, compartida (COL.11)
//
// Nació dentro de `pages/Reportes.js` en COL.10. Cuando Dashboard y Métricas tuvieron
// que declarar lo mismo, la salida barata era copiarlo dos veces — y tres copias del
// mismo texto son tres sitios que se desincronizan, que es literalmente el defecto que
// este repo lleva persiguiendo desde DT.235 (y el de `DOCTOR_PLAN_LIMITS`, que sigue
// duplicado desde S10). Así que se extrajo: `Reportes.js` lo importa de aquí, no lo
// define.
//
// 🔴 LO QUE DECLARA Y POR QUÉ. La ausencia de filas en `user_doctor_scope` significa
//    TODA la clínica (COL.8), que es contraintuitivo; y el backend manda `alcance.total`
//    ya RESUELTO para que ninguna pantalla lo deduzca de una lista vacía. Aquí sólo se
//    pinta lo que llega.
//
// ⚠️ `acotadas` sólo lo mandan los endpoints de `/metrics/*`, y su ausencia es
//    significativa, no un hueco: en los reportes el motor ANULA los campos que no
//    aplican (y por eso llegan en `no_aplican`), mientras que en métricas los FILTRA —
//    la cifra sigue siendo cierta, lo que cambia es de quién habla. El banner lo dice
//    con palabras distintas en cada caso en vez de fundir las dos cosas.
// ═══════════════════════════════════════════════════════════════════════════════

export default function BannerAlcance({ alcance, mezcla }) {
  if (!alcance) return null;

  if (alcance.total) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-start gap-2"
        data-testid="alcance-total"
      >
        <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Calculado sobre <strong>toda la clínica</strong>: todos los médicos y también lo
          que no está imputado a ninguno.
        </span>
      </div>
    );
  }

  const n = (alcance.doctores || []).length;
  const medicos = n === 1 ? "el médico" : `los ${n} médicos`;
  const asignados = n === 1 ? "asignado" : "asignados";

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
      data-testid="alcance-acotado"
    >
      <Stethoscope className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>
        Calculado <strong>sólo sobre lo de {medicos} que tienes {asignados}</strong>.
        {mezcla ? (
          <>
            {" "}
            ⚠️ En esta pantalla <strong>no todas las cifras son tuyas</strong>: las de
            citas sí, y las de pacientes y conversaciones son de la clínica entera —cada
            tarjeta lo dice—. Las cifras que no te corresponden no se calculan; no salen
            en cero.
          </>
        ) : (
          <>
            {" "}
            Las cifras de la clínica completa no te corresponden, así que no se calculan —
            no salen en cero.
          </>
        )}
      </span>
    </div>
  );
}

/** ¿Este campo concreto viene acotado por el alcance del actor?
 *
 *  🔴 Se pregunta por el NOMBRE que manda el backend, no por «el valor parece pequeño»
 *     ni por una lista escrita aquí: sólo el servidor sabe de qué tabla sale cada cifra
 *     y cuáles llevan política RESTRICTIVE. Si mañana `patients` recibiera alcance, esto
 *     sigue siendo correcto sin tocar una línea. */
export const esAcotada = (alcance, campo) =>
  !!alcance && !alcance.total && (alcance.acotadas || []).includes(campo);
